/**
 * The words and the filtering of Administración → «Fiabilidad» (contract v10.38, ADR 0032 and
 * ADR 0033 of the back), apart from the screen so they can be tested on their own.
 */
import { MODE_LABELS } from '@/shared/triage/vocabulary';
import type { Tone } from '@/shared/ui/Badge';

import type {
  ReliabilityMailbox,
  ReliabilityPerson,
  ReliabilityReport,
  ReliabilityRow,
} from './types';

/** What an error costs, as the shop would say it. `Record`: a new tier stops compiling. */
export const TIER_LABELS: Record<ReliabilityRow['tier'], string> = {
  dinero: 'Dinero',
  reenvio: 'Se reenvía a alguien',
  se_queda: 'Se queda en el buzón',
};

const percent = new Intl.NumberFormat('es-ES', { style: 'percent', maximumFractionDigits: 1 });

export function formatPercent(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : percent.format(value);
}

/** «Lista para automático», «Faltan 12 bien seguidas», «No llega». */
export function verdictText(row: ReliabilityRow): { text: string; tone: Tone } {
  if (row.verdict === 'lista') {
    return row.in_auto
      ? { text: 'Lista y ya en automático', tone: 'ok' }
      : { text: 'Lista para automático', tone: 'ok' };
  }
  const missing = row.missing_answers ?? 0;
  if (row.verdict === 'faltan_respuestas') {
    return {
      text: `Faltan ${missing} ${missing === 1 ? 'respuesta bien' : 'respuestas bien seguidas'}`,
      tone: 'info',
    };
  }
  return { text: 'No llega', tone: 'warn' };
}

/** «117 de 119 (98,3 %)». */
export function rightText(row: ReliabilityRow): string {
  return `${row.ok} de ${row.answered} (${formatPercent(row.rate)})`;
}

/** «95 % · 32 seguidas sin fallo desde cero». */
export function barText(row: ReliabilityRow): string {
  return `${formatPercent(row.bar)} · ${row.min_answers} seguidas sin fallo desde cero`;
}

/** «Reenvío: Al confirmar · Carpeta: Automático», what Activación says today. */
export function modesText(row: ReliabilityRow): string {
  if (!row.forward_mode && !row.label_mode) return '—';
  const forward = row.forward_mode ? MODE_LABELS[row.forward_mode] : '—';
  const label = row.label_mode ? MODE_LABELS[row.label_mode] : '—';
  return `Reenvío: ${forward} · Carpeta: ${label}`;
}

/** «Víctor <victor@ferreteriacubas.com>, administra». */
export function personText(person: ReliabilityPerson): string {
  return `${person.name} <${person.email}>${person.is_admin ? ', administra' : ''}`;
}

/** «Víctor (98), María (3)»; an account that no longer exists is «una cuenta borrada». */
export function answeredByText(row: ReliabilityRow): string {
  const who = row.answered_by ?? [];
  if (who.length === 0) return '—';
  return who
    .map((each) => `${each.person ? each.person.name : 'una cuenta borrada'} (${each.answers})`)
    .join(', ');
}

/** Every account the report names, for the «Cuenta» filter: who has a mailbox and who
 * answered, once each, by name. */
export function accountsOf(report: ReliabilityReport): ReliabilityPerson[] {
  const found = new Map<string, ReliabilityPerson>();
  for (const mailbox of report.mailboxes) {
    for (const account of mailbox.accounts ?? []) found.set(account.id, account);
  }
  for (const row of report.rows) {
    for (const each of row.answered_by ?? []) {
      if (each.person && !found.has(each.person.id)) found.set(each.person.id, each.person);
    }
  }
  return [...found.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export interface ReliabilityView {
  /** `''` is every account. */
  account: string;
  /** `''` is every mailbox. */
  mailbox: string;
}

/** The mailboxes the filters leave: a chosen account keeps the mailboxes it has and the ones
 * whose proposals it answered. */
export function visibleMailboxes(
  report: ReliabilityReport,
  view: ReliabilityView,
): ReliabilityMailbox[] {
  return report.mailboxes.filter((mailbox) => {
    if (view.mailbox && mailbox.mailbox !== view.mailbox) return false;
    if (!view.account) return true;
    const has = (mailbox.accounts ?? []).some((account) => account.id === view.account);
    const answered = report.rows.some(
      (row) =>
        row.mailbox === mailbox.mailbox &&
        (row.answered_by ?? []).some((each) => each.person?.id === view.account),
    );
    return has || answered;
  });
}

/** The notices: ready for automatic and not acting on its own yet, in the visible mailboxes. */
export function readyRows(report: ReliabilityReport, mailboxes: string[]): ReliabilityRow[] {
  return report.rows.filter(
    (row) => row.verdict === 'lista' && !row.in_auto && mailboxes.includes(row.mailbox),
  );
}
